/**
 * logger.js — Logger centralizado para Vortex Web
 *
 * En producción, todos los console.log/warn/error son suprimidos para
 * usuarios normales. Solo se muestran si el usuario es admin (verificado
 * en Firebase RTDB bajo el nodo /admins/{uid}).
 *
 * Uso en cualquier componente:
 *   import logger from '../utils/logger';
 *   logger.log('Mensaje de debug');
 *   logger.warn('Advertencia');
 *   logger.error('Error');
 *
 * También se sobreescribe window.console globalmente en main.jsx
 * para suprimir los console.log nativos de dependencias.
 */

const isDev = import.meta.env.DEV;

// Estado interno del logger
let _isAdmin = false;
let _initialized = false;

// Referencia a los métodos originales de consola (guardados antes de cualquier override)
const _nativeLog = console.log.bind(console);
const _nativeWarn = console.warn.bind(console);
const _nativeError = console.error.bind(console);
const _nativeInfo = console.info.bind(console);
const _nativeDebug = console.debug.bind(console);

/**
 * Establece si el usuario actual es admin.
 * Llamado desde App.jsx una vez que se conoce el estado de auth.
 */
export function setAdminStatus(isAdmin) {
    _isAdmin = Boolean(isAdmin);
    _initialized = true;
    applyConsoleOverride();
}

/**
 * Indica si los logs están activos (en desarrollo siempre sí).
 */
export function isLoggingEnabled() {
    return isDev || _isAdmin;
}

/**
 * Sobreescribe window.console para suprimir output en producción/no-admin.
 */
function applyConsoleOverride() {
    if (isDev) return; // En desarrollo siempre mostramos todo

    if (_isAdmin) {
        // Admin: restaurar consola completa
        console.log = _nativeLog;
        console.warn = _nativeWarn;
        console.error = _nativeError;
        console.info = _nativeInfo;
        console.debug = _nativeDebug;
    } else {
        // No-admin en producción: silenciar todo excepto errores críticos
        const noop = () => {};
        console.log = noop;
        console.warn = noop;
        console.info = noop;
        console.debug = noop;
        // console.error se mantiene para no ocultar errores reales del navegador
        console.error = noop;
    }
}

// Logger explícito para uso en componentes propios
const logger = {
    log: (...args) => {
        if (isDev || _isAdmin) _nativeLog('[LOG]', ...args);
    },
    warn: (...args) => {
        if (isDev || _isAdmin) _nativeWarn('[WARN]', ...args);
    },
    error: (...args) => {
        if (isDev || _isAdmin) _nativeError('[ERROR]', ...args);
    },
    info: (...args) => {
        if (isDev || _isAdmin) _nativeInfo('[INFO]', ...args);
    },
    debug: (...args) => {
        if (isDev || _isAdmin) _nativeDebug('[DEBUG]', ...args);
    },
};

// Aplicar override inicial antes de que el estado de auth esté disponible.
// En producción, suprimimos todo hasta saber si es admin.
if (!isDev) {
    const noop = () => {};
    console.log = noop;
    console.warn = noop;
    console.info = noop;
    console.debug = noop;
    console.error = noop;
}

export default logger;
